import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const FlatPlanEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [flatPlan, setFlatPlan] = useState(null);
  const [adSizes, setAdSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedAdSize, setDraggedAdSize] = useState(null);
  const [selectedPage, setSelectedPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [planRes, adSizesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/flat-plans/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/ad-sizes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setFlatPlan(planRes.data);
      setAdSizes(adSizesRes.data.filter(size => size.enabled));
    } catch (error) {
      console.error('Error loading flat plan:', error);
      toast.error('Failed to load flat plan');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = adSize => {
    setDraggedAdSize(adSize);
  };

  const handleDrop = (pageNumber, e) => {
    e.preventDefault();
    if (!draggedAdSize) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newAd = {
      adSize: draggedAdSize._id,
      adSizeName: draggedAdSize.displayName,
      position: {
        x: Math.max(0, Math.min(x, 90)),
        y: Math.max(0, Math.min(y, 90)),
        width: (draggedAdSize.horizontalSize.width / 120) * 100,
        height: (draggedAdSize.horizontalSize.height / 80) * 100,
      },
      color: getRandomColor(),
      status: 'new',
    };

    const updatedPages = flatPlan.pages.map(page => {
      if (page.pageNumber === pageNumber) {
        return { ...page, ads: [...page.ads, newAd] };
      }
      return page;
    });

    setFlatPlan({ ...flatPlan, pages: updatedPages });
    setDraggedAdSize(null);
  };

  const handleRemoveAd = (pageNumber, adIndex) => {
    const updatedPages = flatPlan.pages.map(page => {
      if (page.pageNumber === pageNumber) {
        return { ...page, ads: page.ads.filter((_, i) => i !== adIndex) };
      }
      return page;
    });

    setFlatPlan({ ...flatPlan, pages: updatedPages });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/flat-plans/${id}`,
        { pages: flatPlan.pages, status: flatPlan.status },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Flat plan saved');
    } catch (error) {
      toast.error('Failed to save flat plan');
    }
  };

  const handleSendToDesigner = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/flat-plans/${id}`,
        { pages: flatPlan.pages, status: 'sent_to_designer' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Flat plan sent to designer');
      navigate('/flat-plans');
    } catch (error) {
      toast.error('Failed to send to designer');
    }
  };

  const getRandomColor = () => {
    const colors = ['#9333ea', '#22c55e', '#ef4444', '#f59e0b', '#3b82f6', '#ec4899'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  if (loading || !flatPlan) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  const currentPage = flatPlan.pages.find(p => p.pageNumber === selectedPage);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">
            {flatPlan.magazine.name} - {flatPlan.issue}
          </h1>
          <p className="text-gray-600">Total Pages: {flatPlan.totalPages}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Save
          </button>
          <button
            onClick={handleSendToDesigner}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Send to Designer
          </button>
          <button
            onClick={() => navigate('/flat-plans')}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
          >
            Back
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Ad Sizes Sidebar */}
        <div className="col-span-3 bg-white rounded-lg shadow p-4 max-h-screen overflow-y-auto">
          <h2 className="text-xl font-bold mb-4">Available Ads</h2>
          {adSizes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">No ad sizes available</p>
              <p className="text-sm text-gray-400">Go to Settings → Ad Sizes to enable ad sizes</p>
            </div>
          ) : (
            adSizes.map(adSize => (
              <div
                key={adSize._id}
                draggable
                onDragStart={() => handleDragStart(adSize)}
                className="bg-gray-100 p-3 mb-2 rounded cursor-move hover:bg-gray-200 transition-colors"
              >
                <p className="font-semibold">{adSize.displayName}</p>
                <p className="text-sm text-gray-600">{adSize.name}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {adSize.horizontalSize.width}mm × {adSize.horizontalSize.height}
                  mm
                </p>
              </div>
            ))
          )}
        </div>

        {/* Main Editor */}
        <div className="col-span-9">
          {/* Page Selector */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex gap-2 flex-wrap">
              {flatPlan.pages.map(page => (
                <button
                  key={page.pageNumber}
                  onClick={() => setSelectedPage(page.pageNumber)}
                  className={`px-3 py-1 rounded ${
                    selectedPage === page.pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {page.pageNumber}
                </button>
              ))}
            </div>
          </div>

          {/* Page Canvas */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Page {selectedPage}</h3>
            <div
              className="relative bg-gray-50 border-2 border-dashed border-gray-300 rounded"
              style={{ width: '100%', paddingBottom: '66.67%' }}
              onDrop={e => handleDrop(selectedPage, e)}
              onDragOver={e => e.preventDefault()}
            >
              {currentPage?.ads.map((ad, idx) => (
                <div
                  key={idx}
                  className="absolute rounded shadow-lg flex items-center justify-center text-white text-xs font-semibold cursor-pointer group"
                  style={{
                    left: `${ad.position.x}%`,
                    top: `${ad.position.y}%`,
                    width: `${ad.position.width}%`,
                    height: `${ad.position.height}%`,
                    backgroundColor: ad.color,
                  }}
                  onClick={() => handleRemoveAd(selectedPage, idx)}
                >
                  <span className="text-center p-1">{ad.adSizeName || 'Ad'}</span>
                  <button className="absolute top-0 right-0 bg-red-600 text-white rounded-full w-5 h-5 hidden group-hover:flex items-center justify-center">
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlatPlanEditor;
